import { definePermissions, ExpressionBuilder } from "@rocicorp/zero";
import { createZeroSchema } from "drizzle-zero";

import * as drizzleSchema from "./db/schema";

export const schema = createZeroSchema(drizzleSchema, {
  tables: {
    users: {
      id: true,
      createdAt: true,
      updatedAt: true,
      telegramId: true,
      telegramUsername: true,
      name: true,
      avatarUrl: true,
      avatarHash: false,
    },
    refreshTokens: false,
    workspaces: {
      id: true,
      createdAt: true,
      updatedAt: true,
      slug: true,
      name: true,
    },
    workspaceMemberships: {
      workspaceId: true,
      userId: true,
      createdAt: true,
      updatedAt: true,
      role: true,
    },
    boards: {
      id: true,
      createdAt: true,
      updatedAt: true,
      workspaceId: true,
      name: true,
      boardKey: true,
      visibility: true,
      icon: true,
      iconColor: true,
    },
    boardMemberships: {
      boardId: true,
      userId: true,
      createdAt: true,
      updatedAt: true,
      role: true,
    },
    lists: {
      id: true,
      createdAt: true,
      updatedAt: true,
      boardId: true,
      name: true,
      position: true,
    },
    tasks: {
      id: true,
      createdAt: true,
      updatedAt: true,
      boardId: true,
      taskNumber: true,
      listId: true,
      position: true,
      name: true,
      description: true,
    },
    taskAssignments: {
      taskId: true,
      userId: true,
    },
  },
  manyToMany: {
    workspaces: {
      members: ["workspaceMemberships", "users"],
    },
    boards: {
      members: ["boardMemberships", "users"],
    },
    tasks: {
      assignees: ["taskAssignments", "users"],
    },
  },
});

export type AuthData = {
  sub: string;
};
export type Schema = typeof schema;

export const permissions = definePermissions<AuthData, Schema>(schema, () => {
  const canViewUser = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "users">,
  ) =>
    eb.or(
      canEditUser(authData, eb),
      eb.exists("boardMemberships", (q) =>
        q.whereExists("board", (bq) =>
          bq.where((eb) => canViewBoard(authData, eb)),
        ),
      ),
    );

  const canEditUser = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "users">,
  ) => eb.cmp("id", "=", authData.sub);

  const canViewWorkspace = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "workspaces">,
  ) =>
    eb.exists("memberships", (q) =>
      q.where((eb) => eb.cmp("userId", "=", authData.sub)),
    );

  const canViewBoard = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "boards">,
  ) =>
    eb.exists("memberships", (mq) =>
      mq.where((eb) => eb.cmp("userId", "=", authData.sub)),
    );

  const canViewBoardEntity = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "lists" | "tasks" | "boardMemberships">,
  ) => eb.exists("board", (q) => q.where((eb) => canViewBoard(authData, eb)));

  const canViewTaskAssignee = (
    authData: AuthData,
    eb: ExpressionBuilder<Schema, "taskAssignments">,
  ) =>
    eb.exists("task", (q) => q.where((eb) => canViewBoardEntity(authData, eb)));

  return {
    workspaces: {
      row: {
        select: [canViewWorkspace],
      },
    },
    users: {
      row: {
        select: [canViewUser],
      },
    },
    boards: {
      row: {
        select: [canViewBoard],
      },
    },
    boardMemberships: {
      row: {
        select: [canViewBoardEntity],
      },
    },
    lists: {
      row: {
        select: [canViewBoardEntity],
      },
    },
    tasks: {
      row: {
        select: [canViewBoardEntity],
      },
    },
    taskAssignments: {
      row: {
        select: [canViewTaskAssignee],
      },
    },
  };
});
